import { Box, Text } from '@user-portal/wikr-components';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { sendAnalyticCancelOfferClick, trackScreenLoadCancelOffer } from 'services/Analytics';

import { useSelector } from 'hooks';
import useModals from 'modals/hooks';
import { ModalName } from 'modals/types';

import { getValuesByKey } from 'helpers/utils';

import OutlinedButton from 'components/Buttons/OutlinedButton';
import Modal from 'components/Modal';

import { CancelFlowOffers, CancelOfferAction, SubscriptionPauseType } from 'types/subscription';

import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';
import FullScreenModalHeader from '../../components/FullScreenModalHeader';

import Star from './img/star.svg';

import * as S from './styled';

const PauseModal = () => {
    const { closeModal, openModal } = useModals();
    const { t } = useTranslation();

    const cancellationCandidates = useSelector(selectCancellationCandidates);

    const { onCancelSubscription, onRequestPause } = useCancelSubscriptionFlow();

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    useEffect(() => {
        trackScreenLoadCancelOffer({
            eventLabel: { subscription_id, offer: CancelFlowOffers.Pause },
        });
    }, [subscription_id]);

    const sendAnalytics = useCallback(
        (actionEvent: CancelOfferAction) => {
            sendAnalyticCancelOfferClick({
                actionEvent,
                eventLabel: { subscription_id, offer: CancelFlowOffers.Pause },
            });
        },
        [subscription_id]
    );

    const onBackButtonClick = () => {
        sendAnalytics(CancelOfferAction.Close);

        openModal(ModalName.CancellationReasonModal);
    };

    const onCloseButtonClick = useCallback(() => {
        sendAnalytics(CancelOfferAction.Close);

        closeModal();
    }, [closeModal, sendAnalytics]);

    const onPauseButtonClick = () => {
        sendAnalytics(CancelOfferAction.Accept);

        onRequestPause(SubscriptionPauseType.Pause);
    };

    const onCancelButtonClick = () => {
        sendAnalytics(CancelOfferAction.Cancel);
        onCancelSubscription();
    };

    return (
        <Modal
            fullscreen
            isOpen
            onClose={onCloseButtonClick}
            customStyles={{ padding: 0 }}
            withRoundedCorners={false}
            dataTestId="PauseModalControl"
        >
            <FullScreenModalHeader onBackButtonClick={onBackButtonClick} closeModal={onCloseButtonClick} />

            <div className="container-sm">
                <Box $paddingTop={32} $paddingBottom={32}>
                    <Text medium center mb={12} type="h4">
                        {t('subscription.pauseModal.title')}
                    </Text>
                    <Text center mb={24} type="large-text">
                        {t('subscription.pauseModal.subtitle')}
                    </Text>

                    <S.Card data-locator="pauseSubscriptionDealBlock">
                        <S.CardHeader color="decorative-c" medium center>
                            {t('subscription.pauseModal.card.label')}
                        </S.CardHeader>
                        <S.CardContent>
                            <Text medium center mb={20} type="h5">
                                {t('subscription.pauseModal.card.title')}
                            </Text>
                            <S.ListItem>
                                <S.ItemImage>
                                    <Star />
                                </S.ItemImage>
                                <Text>{t('subscription.pauseModal.card.item1')}</Text>
                            </S.ListItem>
                            <S.ListItem>
                                <S.ItemImage>
                                    <Star />
                                </S.ItemImage>
                                <Text>{t('subscription.pauseModal.card.item2')}</Text>
                            </S.ListItem>
                            <S.ListItem>
                                <S.ItemImage>
                                    <Star />
                                </S.ItemImage>
                                <Text>{t('subscription.pauseModal.card.item3')}</Text>
                            </S.ListItem>

                            <S.PauseButton onClick={onPauseButtonClick} dataLocator="pauseSubscriptionButton">
                                {t('subscription.pauseModal.pauseBtn')}
                            </S.PauseButton>
                        </S.CardContent>
                    </S.Card>
                    <OutlinedButton
                        onClick={onCancelButtonClick}
                        text={t('basics.button.cancelSubscription')}
                        backgroundColor="surface-main"
                        dataLocator="cancelSubscriptionButton"
                    />
                </Box>
            </div>
        </Modal>
    );
};

export default PauseModal;
